<script setup>
import { computed, nextTick, ref } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { usePwaInstall } from 'dashboard/composables/usePwaInstall';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';
import MelhoriaModal from './MelhoriaModal.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { useSidebarDropdownAnchor } from './useSidebarDropdownAnchor';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

// Preferência top, mas o lado final vem da medida real do menu.
const { triggerRef, menuRef, anchorStyle, menuMaxHeight, measure, remeasure } =
  useSidebarDropdownAnchor({ placement: 'top' });

const openProfileMenu = toggle => {
  measure();
  toggle();
  nextTick(() => remeasure());
};

// SiteUp E4.3/fix: o scale-in do DropdownContainer não alcança este menu —
// ele é teleportado pro <body>, e o Transition do container anima uma casca
// vazia; a animação roda aqui. O isOpen vem como PROP DO SLOT default (ver
// template): useDropdownContext() aqui no pai do container explodia no setup
// (inject não enxerga provide de um filho) e derrubava o componente inteiro.
const showMelhoria = ref(false);

const { canInstall, promptInstall } = usePwaInstall();

const DOCS_URL = 'https://www.siteup.com.br/ajuda';

const openDocs = () => {
  window.open(DOCS_URL, '_blank', 'noopener');
};

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-ph-lifebuoy',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-ph-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-ph-pencil-simple',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-ph-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: 'Sugerir melhoria',
      icon: 'i-ph-lightbulb',
      click: () => {
        showMelhoria.value = true;
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-ph-book-open',
      click: openDocs,
    },
    {
      show: canInstall.value,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.INSTALL_APP'),
      icon: 'i-ph-download-simple',
      click: async () => {
        await promptInstall();
        emit('close');
      },
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-ph-buildings',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-ph-sign-out',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCollapsed ? 'w-auto' : 'w-full'"
    :ignore-click-outside="['[data-sidebar-profile-menu]']"
    open-upward
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        ref="triggerRef"
        v-tooltip="isCollapsed ? currentUser.available_name : null"
        class="flex min-h-11 min-w-11 gap-2 items-center p-1 text-left rounded-xl cursor-pointer hover:bg-n-alpha-1"
        :class="[
          { 'bg-n-alpha-1': isOpen },
          isCollapsed ? 'justify-center' : 'w-full',
        ]"
        :aria-label="t('SIDEBAR_ITEMS.ACCOUNT_MENU')"
        @click="() => openProfileMenu(toggle)"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
        />
        <div v-if="!isCollapsed" class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <template #default="{ isOpen }">
      <TeleportWithDirection>
        <Transition
          appear
          enter-active-class="transition duration-[var(--motion-duration-enter)] ease-[var(--motion-ease-out)] origin-bottom"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-[var(--motion-duration-press)] ease-[var(--motion-ease-out)] origin-bottom"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="isOpen"
            ref="menuRef"
            data-sidebar-profile-menu
            class="fixed z-[100]"
            :style="anchorStyle"
          >
            <!-- !static: o DropdownBody é `absolute` por padrão, e filho
              absoluto não conta na altura do pai. O wrapper media 0px, o
              posicionamento era feito pelo `bottom-0` (painel crescendo pra
              cima, fora da tela) e o teto de altura nunca casava com o
              conteúdo. Em fluxo, o wrapper tem a medida real do painel. -->
            <DropdownBody
              class="!static overflow-y-auto w-80"
              :style="{ maxHeight: menuMaxHeight }"
            >
              <SidebarProfileMenuStatus />
              <DropdownSeparator />
              <template v-for="item in allowedMenuItems" :key="item.label">
                <CustomBrandPolicyWrapper
                  :show-on-custom-branded-instance="
                    item.showOnCustomBrandedInstance
                  "
                >
                  <DropdownItem v-if="item.show" v-bind="item" />
                </CustomBrandPolicyWrapper>
              </template>
            </DropdownBody>
          </div>
        </Transition>
      </TeleportWithDirection>
    </template>
  </DropdownContainer>
  <MelhoriaModal v-if="showMelhoria" @close="showMelhoria = false" />
</template>
